import { useMemo } from 'react';
import { reloadForFailedChunk, noteChunkLoadSuccess, reloadBlocked } from '@/lib/chunkReload';
import { isStaleBuild } from '@/lib/newVersion';
import { createBrowserRouter, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import LocaleLayout from './layouts/LocaleLayout';
import RouteErrorBoundary, { NotFound } from './components/RouteErrorBoundary';
import { resolveCatchAll } from '@/lib/catchAllRoute';
import { locales } from './i18n';

// Locale-aware path resolver. The dashboard lives under /:locale/dashboard,
// so a redirect target like "/dashboard/studio?..." would strip the user's
// locale prefix (Hebrew users would land on /en after the catch-all). We
// always recompute the locale segment from the current pathname.
function useLocaleSegment(): string {
  const location = useLocation();
  const seg = location.pathname.split('/')[1] || '';
  return locales.includes(seg as any) ? seg : 'en';
}

function LocaleNavigate({ path, replace }: { path: string; replace?: boolean }) {
  const loc = useLocaleSegment();
  return <Navigate to={`/${loc}${path}`} replace={replace} />;
}

// Helper: path-param → query-string redirect. React Router's <Navigate to>
// is a static string, so for dynamic redirects (e.g. /video-project/:id →
// /studio?page=story&projectId=:id) we wrap useParams in a tiny component.
// Locale prefix preserved so /he/... stays in /he/...
function StudioParamRedirect({ page, paramName }: { page: 'story' | 'edit'; paramName: string }) {
  const params = useParams();
  const loc = useLocaleSegment();
  const id = params[paramName];
  const target = id
    ? `/${loc}/dashboard/studio?page=${page}&projectId=${encodeURIComponent(id)}`
    : `/${loc}/dashboard/studio?page=${page}`;
  return <Navigate to={target} replace />;
}

// Lazy wrapper: React Router lazy() expects { Component }
// On chunk load failure (stale deploy), reload the page ONCE to get fresh
// assets — see lib/chunkReload for why the retry is strictly bounded.
const lazy = (importFn: () => Promise<any>) => ({
  lazy: async () => {
    try {
      const m = await importFn();
      noteChunkLoadSuccess();
      return { Component: m.default };
    } catch (err) {
      if (reloadForFailedChunk()) {
        return await new Promise<never>(() => {}); // never resolves — page is reloading
      }
      throw err;
    }
  },
});

// Timezone → locale fallback for when browser language is English but user is in a non-English country
const TZ_LOCALE: Record<string, string> = {
  'Asia/Jerusalem': 'he', 'Asia/Tel_Aviv': 'he', 'Asia/Tokyo': 'ja', 'Asia/Seoul': 'ko',
  'Asia/Shanghai': 'zh-CN', 'Asia/Hong_Kong': 'zh-HK', 'Asia/Taipei': 'zh-TW',
  'Asia/Bangkok': 'th', 'Asia/Ho_Chi_Minh': 'vi', 'Asia/Jakarta': 'id', 'Asia/Kolkata': 'hi',
  'Asia/Calcutta': 'hi', 'Asia/Dhaka': 'bn', 'Asia/Karachi': 'ur', 'Asia/Tehran': 'fa',
  'Asia/Dubai': 'ar', 'Asia/Riyadh': 'ar', 'Asia/Baghdad': 'ar', 'Asia/Kuwait': 'ar',
  'Asia/Almaty': 'kk', 'Asia/Tashkent': 'uz', 'Asia/Tbilisi': 'ka', 'Asia/Yerevan': 'hy',
  'Asia/Baku': 'az', 'Asia/Colombo': 'si', 'Asia/Kathmandu': 'ne', 'Asia/Phnom_Penh': 'km',
  'Asia/Vientiane': 'lo', 'Asia/Yangon': 'my', 'Asia/Ulaanbaatar': 'mn', 'Asia/Manila': 'tl',
  'Asia/Kuala_Lumpur': 'ms', 'Asia/Bishkek': 'ky', 'Asia/Dushanbe': 'tg',
  'Europe/Moscow': 'ru', 'Europe/Kiev': 'uk', 'Europe/Warsaw': 'pl', 'Europe/Berlin': 'de',
  'Europe/Vienna': 'de', 'Europe/Zurich': 'de', 'Europe/Paris': 'fr', 'Europe/Rome': 'it',
  'Europe/Madrid': 'es', 'Europe/Lisbon': 'pt', 'Europe/Amsterdam': 'nl',
  'Europe/Brussels': 'nl', 'Europe/Stockholm': 'sv', 'Europe/Oslo': 'nb',
  'Europe/Copenhagen': 'da', 'Europe/Helsinki': 'fi', 'Europe/Athens': 'el',
  'Europe/Bucharest': 'ro', 'Europe/Sofia': 'bg', 'Europe/Prague': 'cs',
  'Europe/Budapest': 'hu', 'Europe/Belgrade': 'sr', 'Europe/Zagreb': 'hr',
  'Europe/Ljubljana': 'sl', 'Europe/Bratislava': 'sk', 'Europe/Tallinn': 'et',
  'Europe/Riga': 'lv', 'Europe/Vilnius': 'lt', 'Europe/Minsk': 'be',
  'Europe/Istanbul': 'tr', 'Europe/Skopje': 'mk', 'Europe/Tirana': 'sq',
  'Europe/Reykjavik': 'is', 'Europe/Dublin': 'ga',
  'America/Sao_Paulo': 'pt-BR', 'America/Argentina/Buenos_Aires': 'es-LA',
  'America/Mexico_City': 'es-LA', 'America/Bogota': 'es-LA', 'America/Lima': 'es-LA',
  'America/Santiago': 'es-LA', 'America/Caracas': 'es-LA', 'America/Montevideo': 'es-LA',
  'Africa/Cairo': 'ar', 'Africa/Lagos': 'yo', 'Africa/Nairobi': 'sw',
  'Africa/Casablanca': 'ar', 'Africa/Addis_Ababa': 'am', 'Africa/Kigali': 'rw',
};

// `suffix` keeps a path below the locale (with its query), for entry points
// that partners link to without one — /oauth/authorize?… must land on
// /<locale>/oauth/authorize?… with every parameter intact.
function BrowserLocaleRedirect({ suffix = '' }: { suffix?: string }) {
  const { search } = useLocation();
  const detected = useMemo(() => {
    const stored = localStorage.getItem('soltik-locale');
    if (stored && locales.includes(stored)) return stored;

    // Check browser language preferences
    const browserLangs = navigator.languages || [navigator.language];
    let browserResult: string | null = null;
    for (const bl of browserLangs) {
      const lower = bl.toLowerCase();
      const exact = locales.find(c => c.toLowerCase() === lower);
      if (exact) { browserResult = exact; break; }
      const base = lower.split('-')[0];
      const baseMatch = locales.find(c => c === base || c.toLowerCase().startsWith(base + '-'));
      if (baseMatch) { browserResult = baseMatch; break; }
    }

    // If browser gives a non-English locale, use it
    if (browserResult && browserResult !== 'en' && browserResult !== 'en-GB') return browserResult;

    // Fallback: detect locale from timezone
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && TZ_LOCALE[tz] && locales.includes(TZ_LOCALE[tz])) return TZ_LOCALE[tz];
    } catch {}

    return browserResult || 'en';
  }, []);
  return <Navigate to={suffix ? `/${detected}${suffix}${search}` : `/${detected}`} replace />;
}

// The catch-all's loader. Runs before the element, including on a cold load, so
// a path that already carries a locale and matches nothing raises a real 404
// instead of rendering a redirect to itself. A Response thrown from a loader is
// what makes `isRouteErrorResponse(error) && error.status === 404` true in
// RouteErrorBoundary, which is how its "Page not found" state is reached.
function catchAllLoader({ request }: { request: Request }) {
  const url = new URL(request.url);
  if (resolveCatchAll(url.pathname, url.search, url.hash).kind === 'not-found') {
    throw new Response('Not Found', { status: 404, statusText: 'Not Found' });
  }
  return null;
}

function LocaleRedirect() {
  const { pathname, search, hash } = useLocation();
  const resolved = resolveCatchAll(pathname, search, hash);
  // Unreachable while catchAllLoader runs first; rendering the boundary's own
  // screen rather than nothing keeps the blank page from coming back if this
  // element is ever mounted without that loader.
  if (resolved.kind === 'not-found') return <NotFound />;
  return <Navigate to={resolved.to} replace />;
}

// Members-only gate for internal discovery surfaces (explore directories,
// catalogs). The platform is a closed beta: viewing individual *published*
// artifacts (a creator's public agent/profile/shop) and marketing/SEO pages
// stays open, but browsing the whole platform's directory requires an account.
// Client-side gate — the underlying APIs should also require auth for hard
// closure. Logged-out visitors are sent to /login (which links to invite).
function RequireAuth() {
  const { locale } = useParams();
  const authed = typeof window !== 'undefined' && !!localStorage.getItem('authToken');
  if (!authed) return <Navigate to={`/${locale || 'en'}/login`} replace />;
  return <Outlet />;
}

export const router = createBrowserRouter([
  {
    path: '/:locale',
    element: <LocaleLayout />,
    // Without this, a throw anywhere below renders React Router's own default
    // fallback: "Unexpected Application Error!" over a raw stack trace, which
    // is what a customer photographed on production. An errorElement here
    // catches every descendant that has none of its own.
    errorElement: <RouteErrorBoundary />,
    children: [
      // ── Public pages ──
      { index: true, ...lazy(() => import('./pages/Home')) },
      // Dev-only design-system preview. Reachable at /:locale/design-preview
      // in `npm run dev` builds; not registered in production.
      ...(import.meta.env.DEV
        ? [{ path: 'design-preview', ...lazy(() => import('./pages/DesignPreview')) }]
        : []),
      { path: 'login', ...lazy(() => import('./pages/Login')) },
      { path: 'signup', ...lazy(() => import('./pages/Signup')) },
      // Phase 1 — onboarding flow. New signups land here; returning
      // users see the dashboard skip-redirect handled inside the page.
      { path: 'welcome', ...lazy(() => import('./pages/Welcome')) },
      // Suspended account page — submit & review appeals.
      { path: 'banned', ...lazy(() => import('./pages/Banned')) },
      { path: 'security', ...lazy(() => import('./pages/Security')) },
      { path: 'imprint', ...lazy(() => import('./pages/Imprint')) },
      { path: 'request-invite', ...lazy(() => import('./pages/RequestInvite')) },
      { path: 'join', ...lazy(() => import('./pages/RequestInvite')) },
      { path: 'pricing', ...lazy(() => import('./pages/Pricing')) },
      { path: 'privacy', ...lazy(() => import('./pages/Privacy')) },
      { path: 'cookies', ...lazy(() => import('./pages/Cookies')) },
      { path: 'tos', ...lazy(() => import('./pages/Tos')) },
      { path: 'content-policy', ...lazy(() => import('./pages/ContentPolicy')) },
      { path: 'report-image', ...lazy(() => import('./pages/ReportImage')) },
      { path: 'docs', ...lazy(() => import('./pages/Docs')) },
      { path: 'features', ...lazy(() => import('./pages/Features')) },
      { path: 'features/outfits', ...lazy(() => import('./pages/FeatureOutfits')) },
      // Shared outfit collection (owner opt-in, logged-out). Static segments
      // outrank the `:username/:collectionSlug` catch-alls below.
      { path: 'o/c/:slug', ...lazy(() => import('./pages/PublicOutfitCollection')) },
      { path: 'features/sell', ...lazy(() => import('./pages/FeatureSell')) },
      { path: 'features/sites', ...lazy(() => import('./pages/FeatureSites')) },
      { path: 'magic', ...lazy(() => import('./pages/Magic')) },
      // Destination of the email-change confirmation link that
      // POST /user/change-email mails out. Without this route the link 404'd
      // and an email change could never be completed.
      { path: 'confirm-email', ...lazy(() => import('./pages/ConfirmEmail')) },
      { path: 'write', ...lazy(() => import('./pages/Write')) },
      { path: 'for-business', ...lazy(() => import('./pages/ForBusiness')) },
      { path: 'for-creators', ...lazy(() => import('./pages/ForCreators')) },
      { path: 'for-communities', ...lazy(() => import('./pages/ForCommunities')) },
      { path: 'u/:username/:collectionSlug', ...lazy(() => import('./pages/CollectionPage')) },
      { path: 'u/:username/:collectionSlug/podcast/:episodeSlug', ...lazy(() => import('./components/podcast/EpisodePage')) },
      { path: 'u/:username/:collectionSlug/blog', ...lazy(() => import('./pages/SiteBlogListing')) },
      { path: 'u/:username/:collectionSlug/blog/author/:authorId', ...lazy(() => import('./pages/SiteBlogAuthor')) },
      { path: 'u/:username/:collectionSlug/blog/preview/:previewToken', ...lazy(() => import('./pages/SiteBlogPreview')) },
      { path: 'u/:username/:collectionSlug/blog/:postSlug', ...lazy(() => import('./pages/SiteBlogPost')) },
      { path: 'u/:username/:collectionSlug/:subpageSlug', ...lazy(() => import('./pages/CollectionPage')) },
      { path: 'a/:username', ...lazy(() => import('./pages/PublicAgent')) },
      { path: 'u/:username', ...lazy(() => import('./pages/UserProfile')) },
      { path: 'u/:username/:subpageSlug', ...lazy(() => import('./pages/UserProfile')) },
      { path: 'dm/:handle', ...lazy(() => import('./pages/DmRedirect')) },
      { path: 'biz/:handle', ...lazy(() => import('./pages/BusinessPage')) },
      { path: 'biz/:handle/:subpageSlug', ...lazy(() => import('./pages/BusinessPage')) },
      { path: 's/:shareId', ...lazy(() => import('./pages/SharePage')) },
      // Library share links (media_shares tokens): file viewer + Report.
      { path: 'share/:token', ...lazy(() => import('./pages/MediaSharePage')) },
      { path: 'invite/:token', ...lazy(() => import('./pages/InviteRedeem')) },
      // User referral landing. Deliberately NOT under /invite/ (that pattern is
      // the channel invite page above, which shadowed the referral page) nor
      // /join/:code (workspace join). InviteRedeem forwards legacy
      // /invite/<REFERRAL_CODE> links here.
      { path: 'r/:code', ...lazy(() => import('./pages/Invite')) },
      { path: 'c/:shareId', ...lazy(() => import('./pages/CollectionPage')) },
      { path: 'h/:slug', ...lazy(() => import('./pages/HubPage')) },
      { path: 'h/:slug/:subpageSlug', ...lazy(() => import('./pages/HubPage')) },
      { path: 'room/:urlHash', ...lazy(() => import('./pages/Room')) },
      { path: 'join/:code', ...lazy(() => import('./pages/Join')) },
      { path: 'join/site', ...lazy(() => import('./pages/JoinSite')) },
      { path: 'watch/:code', ...lazy(() => import('./pages/Watch')) },
      { path: 'live/:streamId', ...lazy(() => import('./pages/LiveStream')) },
      { path: 'blog', ...lazy(() => import('./pages/Blog')) },
      { path: 'blog/:slug', ...lazy(() => import('./pages/BlogPost')) },
      // Commerce / SEO catalogs stay public (open to sell + be found).
      { path: 'courses', ...lazy(() => import('./pages/BrowseCourses')) },
      { path: 'learning-paths', ...lazy(() => import('./pages/BrowseLearningPaths')) },
      { path: 'live', ...lazy(() => import('./pages/LiveShopping')) },
      // ── Members-only: internal social-discovery directories (closed beta) ──
      {
        element: <RequireAuth />,
        children: [
          { path: 'explore', ...lazy(() => import('./pages/Explore')) },
          { path: 'explore/:category', ...lazy(() => import('./pages/ExploreCategory')) },
        ],
      },
      { path: 'cart/:shopId', ...lazy(() => import('./pages/Cart')) },
      { path: 'shop/checkout/success', ...lazy(() => import('./pages/CheckoutSuccess')) },
      { path: 'shop/checkout/cancel', ...lazy(() => import('./pages/CheckoutCancel')) },
      { path: 'shop/:handle', ...lazy(() => import('./pages/Shop')) },
      { path: 'shop/:handle/:slug', ...lazy(() => import('./pages/Product')) },
      { path: 'course/:slug', ...lazy(() => import('./pages/CourseLanding')) },
      { path: 'shop/:handle/course/:slug', ...lazy(() => import('./pages/CourseLanding')) },
      { path: 'certificates/:certNumber', ...lazy(() => import('./pages/CertificateViewer')) },
      { path: 'auth/google/callback', ...lazy(() => import('./pages/GoogleCallback')) },
      // "Sign in with soltik" — OpenID Connect authorize page for partner apps
      // (AlertBoard). Public: it sends signed-out visitors through /login itself.
      { path: 'oauth/authorize', ...lazy(() => import('./pages/OAuthAuthorize')) },
      // Central sign-in for partner domains: the partner-branded sign-in, then a one-time code
      // back to the partner's domain (pages/PartnerSignIn.tsx).
      { path: 'partner-signin', ...lazy(() => import('./pages/PartnerSignIn')) },
      // Turnstile for the mobile app: the widget can only run in a browser on
      // our own domain, so the app opens this and gets the token back (bug-1524).
      { path: 'auth/turnstile', ...lazy(() => import('./pages/TurnstileBridge')) },
      { path: 'invoice/:invoiceId', ...lazy(() => import('./pages/InvoicePayment')) },
      { path: 'embed/scoops/:id', ...lazy(() => import('./pages/EmbedScoop')) },
      { path: 'embed/collection/:slug', ...lazy(() => import('./pages/EmbedCollection')) },

      // ── Newsletter preferences (public) ──
      { path: 'newsletter/:siteId/preferences/:subscriberId', ...lazy(() => import('./pages/NewsletterPreferences')) },

      // ── Public scoop detail → redirect into dashboard ──
      { path: 'scoops/:id', ...lazy(() => import('./pages/ScoopRedirect')) },

      // ── Vanity URLs: /:username and /:username/:slug ──
      // These must be AFTER all named routes so they don't shadow them
      { path: ':username/:collectionSlug/podcast/:episodeSlug', ...lazy(() => import('./components/podcast/EpisodePage')) },
      { path: ':username/:collectionSlug/blog', ...lazy(() => import('./pages/SiteBlogListing')) },
      { path: ':username/:collectionSlug/blog/author/:authorId', ...lazy(() => import('./pages/SiteBlogAuthor')) },
      { path: ':username/:collectionSlug/blog/preview/:previewToken', ...lazy(() => import('./pages/SiteBlogPreview')) },
      { path: ':username/:collectionSlug/blog/:postSlug', ...lazy(() => import('./pages/SiteBlogPost')) },
      { path: ':username/:collectionSlug/:subpageSlug', ...lazy(() => import('./pages/CollectionPage')) },
      { path: ':username/:collectionSlug', ...lazy(() => import('./pages/CollectionPage')) },
      { path: ':username', ...lazy(() => import('./pages/UserProfile')) },

      // ── Dashboard (authenticated) ──
      {
        path: 'dashboard',
        ...lazy(() => import('./layouts/DashboardLayout')),
        children: [
          { index: true, ...lazy(() => import('./pages/dashboard/Home')) },
          { path: 'feed', ...lazy(() => import('./pages/dashboard/Feed')) },
          { path: 'discover', ...lazy(() => import('./pages/dashboard/DiscoverHub')) },
          { path: 'discover/:slug', ...lazy(() => import('./pages/dashboard/DiscoverDetail')) },
          { path: 'explore', ...lazy(() => import('./pages/dashboard/Explore')) },
          { path: 'outfits', ...lazy(() => import('./pages/dashboard/Marketplace')) },
          { path: 'outfits/analytics', ...lazy(() => import('./pages/dashboard/OutfitAnalytics')) },
          { path: 'marketplace', ...lazy(() => import('./pages/ExploreShops')) },
          { path: 'marketplace/products', ...lazy(() => import('./pages/ExploreProducts')) },
          { path: 'marketplace/courses', ...lazy(() => import('./pages/BrowseCourses')) },
          { path: 'communities', ...lazy(() => import('./pages/dashboard/Scoops')) },
          { path: 'hubs', ...lazy(() => import('./pages/dashboard/Scoops')) }, // legacy redirect
          { path: 'scoops', ...lazy(() => import('./pages/dashboard/Scoops')) },
          // One consolidated "Saved" home — saved posts + extension links + bookmarks + likes,
          // with source filter chips. The old scoops-only Saved page and the orphaned likes
          // page redirect here so no bookmarked URL breaks.
          { path: 'saved', ...lazy(() => import('./pages/dashboard/LibrarySaved')) },
          { path: 'scoops/:id', ...lazy(() => import('./pages/dashboard/ScoopDetail')) },
          { path: 'scoops/:id/settings', ...lazy(() => import('./pages/dashboard/ScoopSettings')) },
          { path: 'scoops/:id/settings/rooms/:roomId', ...lazy(() => import('./pages/dashboard/ScoopRoomSettings')) },
          { path: 'scoops/:id/article/new', ...lazy(() => import('./pages/dashboard/ArticleNew')) },
          { path: 'scoops/:id/article/:articleId', ...lazy(() => import('./pages/dashboard/ArticleEdit')) },
          { path: 'scoops/:id/article/:articleId/read', ...lazy(() => import('./pages/dashboard/ArticleRead')) },
          { path: 'scoops/:id/guides', ...lazy(() => import('./pages/dashboard/ScoopGuides')) },
          { path: 'scoops/:id/store', ...lazy(() => import('./pages/dashboard/ScoopStore')) },
          { path: 'scoop/:id', ...lazy(() => import('./pages/dashboard/ScoopSingle')) },
          { path: 'agent', ...lazy(() => import('./pages/dashboard/Agent')) },
          { path: 'agents', ...lazy(() => import('./pages/dashboard/Agents')) },
          { path: 'collections', ...lazy(() => import('./pages/dashboard/Collections')) },
          { path: 'templates', ...lazy(() => import('./pages/dashboard/TemplateMarketplace')) },
          { path: 'my-templates', ...lazy(() => import('./pages/dashboard/MyTemplates')) },
          { path: 'my-plugins', ...lazy(() => import('./pages/mgt/MyPlugins')) },
          { path: 'developer', ...lazy(() => import('./pages/dashboard/developer/Overview')) },
          { path: 'developer/plugins', ...lazy(() => import('./pages/dashboard/developer/Plugins')) },
          { path: 'developer/templates', ...lazy(() => import('./pages/dashboard/developer/Templates')) },
          { path: 'developer/analytics', ...lazy(() => import('./pages/dashboard/developer/Analytics')) },
          { path: 'developer/connect-ai', ...lazy(() => import('./pages/dashboard/developer/ConnectAI')) },
          { path: 'developer/api-keys', ...lazy(() => import('./pages/dashboard/developer/ApiKeys')) },
          { path: 'developer/docs', ...lazy(() => import('./pages/dashboard/developer/Docs')) },
          { path: 'developer/webhooks', ...lazy(() => import('./pages/dashboard/developer/Webhooks')) },
          { path: 'developer/partner', ...lazy(() => import('./pages/dashboard/developer/Partner')) },
          { path: 'collections/:id', ...lazy(() => import('./pages/dashboard/CollectionDetail')) },
          { path: 'collections/:id/blog', ...lazy(() => import('./pages/dashboard/BlogDashboard')) },
          { path: 'collections/:id/blog/:blogId', ...lazy(() => import('./pages/dashboard/BlogPostEditor')) },
          { path: 'collections/:id/newsletter', ...lazy(() => import('./pages/dashboard/NewsletterDashboard')) },
          { path: 'collections/:id/recommendations', ...lazy(() => import('./pages/dashboard/RecommendationsDashboard')) },
          { path: 'collections/:id/podcasts', ...lazy(() => import('./pages/dashboard/PodcastManager')) },
          { path: 'collections/:id/forms', ...lazy(() => import('./pages/dashboard/FormBuilder')) },
          { path: 'collections/:id/forms/:formId/submissions', ...lazy(() => import('./pages/dashboard/FormSubmissions')) },
          { path: 'collections/:id/seo', ...lazy(() => import('./pages/dashboard/SeoTools')) },
          { path: 'collections/:id/comments', ...lazy(() => import('./pages/dashboard/CommentModeration')) },
          { path: 'collections/:id/calendar', ...lazy(() => import('./pages/dashboard/EditorialCalendar')) },
          { path: 'collections/:id/export', ...lazy(() => import('./pages/dashboard/DataExport')) },
          { path: 'library', ...lazy(() => import('./pages/dashboard/Library')) },
          { path: 'library/saved', element: <LocaleNavigate path="/dashboard/saved" replace /> },
          { path: 'library/likes', element: <LocaleNavigate path="/dashboard/saved?filter=likes" replace /> },
          { path: 'library/tags', ...lazy(() => import('./pages/dashboard/LibraryTags')) },
          { path: 'library/products/:id', ...lazy(() => import('./pages/dashboard/LibraryProduct')) },
          { path: 'messages', ...lazy(() => import('./pages/dashboard/Messages')) },
          { path: 'chats', ...lazy(() => import('./pages/dashboard/Messages')) },
          { path: 'people', ...lazy(() => import('./pages/dashboard/People')) },
          { path: 'friends', ...lazy(() => import('./pages/dashboard/Friends')) },
          { path: 'notifications', ...lazy(() => import('./pages/dashboard/Notifications')) },
          { path: 'billing', ...lazy(() => import('./pages/dashboard/Billing')) },
          { path: 'settings', ...lazy(() => import('./pages/dashboard/Settings')) },
          { path: 'settings/profile', ...lazy(() => import('./pages/dashboard/SettingsProfile')) },
          { path: 'settings/personalization', ...lazy(() => import('./pages/dashboard/SettingsPersonalization')) },
          { path: 'settings/account', ...lazy(() => import('./pages/dashboard/SettingsAccount')) },
          { path: 'settings/billing', ...lazy(() => import('./pages/dashboard/SettingsBilling')) },
          { path: 'settings/notifications', ...lazy(() => import('./pages/dashboard/SettingsNotifications')) },
          { path: 'settings/ai-security', ...lazy(() => import('./pages/dashboard/SettingsAISecurity')) },
          { path: 'settings/devices', ...lazy(() => import('./pages/dashboard/SettingsDevices')) },
          { path: 'settings/privacy', ...lazy(() => import('./pages/dashboard/SettingsPrivacy')) },
          { path: 'settings/blocked', ...lazy(() => import('./pages/dashboard/SettingsBlocked')) },
          { path: 'profile', ...lazy(() => import('./pages/dashboard/Profile')) },
          { path: 'security', ...lazy(() => import('./pages/dashboard/Security')) },
          { path: 'credits', ...lazy(() => import('./pages/dashboard/Credits')) },
          { path: 'shop', ...lazy(() => import('./pages/dashboard/CosmeticShop')) },
          { path: 'stickers/marketplace', ...lazy(() => import('./pages/dashboard/StickerMarketplace')) },
          { path: 'stickers/my-packs', ...lazy(() => import('./pages/dashboard/MyStickerPacks')) },
          { path: 'channels', ...lazy(() => import('./pages/dashboard/ChannelsDiscover')) },
          { path: 'channels/:handle', ...lazy(() => import('./pages/dashboard/ChannelDetail')) },
          // Phase 9.6 — consolidated hub pages. Old URLs keep working; these
          // are new top-level destinations that tile everything under them.
          { path: 'create', ...lazy(() => import('./pages/dashboard/CreateHub')) },
          { path: 'commerce-hub', ...lazy(() => import('./pages/dashboard/CommerceHub')) },
          { path: 'my-library', ...lazy(() => import('./pages/dashboard/LibraryHub')) },
          { path: 'my-posts', ...lazy(() => import('./pages/dashboard/MyPosts')) },
          { path: 'my-posts/:id/analytics', ...lazy(() => import('./pages/dashboard/PostAnalytics')) },
          { path: 'my-articles', ...lazy(() => import('./pages/dashboard/MyArticles')) },
          { path: 'ai', ...lazy(() => import('./pages/dashboard/AI')) },
          { path: 'studio', ...lazy(() => import('./pages/dashboard/Studio')) },
          // Feed-first home candidate, parked alongside the live Home so the two
          // can be compared on staging before either is promoted.
          { path: 'home-next', ...lazy(() => import('./pages/dashboard/HomeNext')) },
          // Old creator entry-points redirect into the unified Studio.
          // LocaleNavigate preserves the /:locale prefix so Hebrew/Arabic
          // users don't get bounced to /en by the catch-all.
          { path: 'ai-builder', element: <LocaleNavigate path="/dashboard/studio?page=generate" replace /> },
          { path: 'video-project', element: <LocaleNavigate path="/dashboard/studio?page=generate&type=creations" replace /> },
          { path: 'video-project/new', element: <LocaleNavigate path="/dashboard/studio?page=story" replace /> },
          { path: 'video-project/:id', element: <StudioParamRedirect page="story" paramName="id" /> },
          { path: 'creative-studio', element: <LocaleNavigate path="/dashboard/studio?page=edit" replace /> },
          { path: 'creative-studio/:projectId', element: <StudioParamRedirect page="edit" paramName="projectId" /> },
          { path: 'prompts', ...lazy(() => import('./pages/dashboard/PromptMarketplace')) },
          { path: 'prompt-marketplace', ...lazy(() => import('./pages/dashboard/PromptMarketplace')) },
          { path: 'my-prompts', ...lazy(() => import('./pages/dashboard/MyPrompts')) },
          { path: 'write', ...lazy(() => import('./pages/dashboard/Write')) },
          { path: 'rooms', ...lazy(() => import('./pages/dashboard/Rooms')) },
          { path: 'bookmarks', ...lazy(() => import('./pages/dashboard/Bookmarks')) },
          { path: 'history', ...lazy(() => import('./pages/dashboard/History')) },
          { path: 'tabs', ...lazy(() => import('./pages/dashboard/Tabs')) },
          { path: 'annotations', ...lazy(() => import('./pages/dashboard/Annotations')) },
          { path: 'activity', ...lazy(() => import('./pages/dashboard/ActivityFeed')) },
          { path: 'analytics', ...lazy(() => import('./pages/dashboard/Analytics')) },
          { path: 'visitor-analytics', ...lazy(() => import('./pages/dashboard/VisitorAnalytics')) },
          { path: 'creator-analytics', ...lazy(() => import('./pages/dashboard/CreatorAnalytics')) },
          { path: 'workspace', ...lazy(() => import('./pages/dashboard/Workspace')) },
          { path: 'reading-list', ...lazy(() => import('./pages/dashboard/ReadingList')) },
          { path: 'watch-party', ...lazy(() => import('./pages/dashboard/WatchParty')) },
          { path: 'live', ...lazy(() => import('./pages/LiveShopping')) },
          { path: 'live-streams', ...lazy(() => import('./pages/dashboard/LiveStreams')) },
          { path: 'go-live/:shopId', ...lazy(() => import('./pages/dashboard/GoLive')) },
          { path: 'go-live/:shopId/:streamId', ...lazy(() => import('./pages/dashboard/GoLive')) },
          { path: 'following', ...lazy(() => import('./pages/dashboard/Following')) },
          { path: 'users/:userId', ...lazy(() => import('./pages/dashboard/UserDetail')) },
          { path: 'stream', ...lazy(() => import('./pages/dashboard/Stream')) },
          { path: 'invitations', ...lazy(() => import('./pages/dashboard/Invitations')) },
          { path: 'pricing', ...lazy(() => import('./pages/dashboard/Pricing')) },
          { path: 'addons', ...lazy(() => import('./pages/dashboard/Addons')) },
          { path: 'business', ...lazy(() => import('./pages/dashboard/Business')) },
          { path: 'creator', ...lazy(() => import('./pages/dashboard/Creator')) },
          { path: 'creator/payments', ...lazy(() => import('./pages/dashboard/CreatorPayments')) },
          { path: 'creator-fund', ...lazy(() => import('./pages/dashboard/CreatorFund')) },
          { path: 'scoop-score', ...lazy(() => import('./pages/dashboard/ScoopScore')) },
          { path: 'affiliates', ...lazy(() => import('./pages/dashboard/Affiliates')) },
          { path: 'commerce', ...lazy(() => import('./pages/dashboard/Commerce')) },
          { path: 'commerce/:shopId/courses/new', ...lazy(() => import('./pages/dashboard/CourseEditor')) },
          { path: 'commerce/:shopId/courses/:courseId/edit', ...lazy(() => import('./pages/dashboard/CourseEditor')) },
          {
            path: 'commerce/:shopId',
            ...lazy(() => import('./layouts/ShopLayout')),
            children: [
              { index: true, ...lazy(() => import('./pages/dashboard/ShopOverview')) },
              { path: 'products', ...lazy(() => import('./pages/dashboard/shop/ProductsPage')) },
              { path: 'orders', ...lazy(() => import('./pages/dashboard/shop/OrdersPage')) },
              { path: 'returns', ...lazy(() => import('./pages/dashboard/shop/ReturnsPage')) },
              { path: 'customers', ...lazy(() => import('./pages/dashboard/shop/CustomersPage')) },
              { path: 'reviews', ...lazy(() => import('./pages/dashboard/shop/ReviewsPage')) },
              { path: 'collections', ...lazy(() => import('./pages/dashboard/shop/CollectionsPage')) },
              { path: 'coupons', ...lazy(() => import('./pages/dashboard/shop/CouponsPage')) },
              { path: 'discounts', ...lazy(() => import('./pages/dashboard/shop/DiscountsPage')) },
              { path: 'gift-cards', ...lazy(() => import('./pages/dashboard/shop/GiftCardsPage')) },
              { path: 'translations', ...lazy(() => import('./pages/dashboard/shop/TranslationsPage')) },
              { path: 'shipping', ...lazy(() => import('./pages/dashboard/shop/ShippingPage')) },
              { path: 'bookings', ...lazy(() => import('./pages/dashboard/shop/BookingsPage')) },
              { path: 'locations', ...lazy(() => import('./pages/dashboard/shop/LocationsPage')) },
              { path: 'recovery', ...lazy(() => import('./pages/dashboard/shop/RecoveryPage')) },
              { path: 'campaigns', ...lazy(() => import('./pages/dashboard/shop/CampaignsPage')) },
              { path: 'automations', ...lazy(() => import('./pages/dashboard/shop/AutomationsPage')) },
              { path: 'subscriptions', ...lazy(() => import('./pages/dashboard/shop/SubscriptionsPage')) },
              { path: 'payments', ...lazy(() => import('./pages/dashboard/shop/PaymentsPage')) },
              { path: 'invoices', ...lazy(() => import('./pages/dashboard/shop/InvoicesPage')) },
              { path: 'staff', ...lazy(() => import('./pages/dashboard/shop/StaffPage')) },
              { path: 'loyalty', ...lazy(() => import('./pages/dashboard/shop/LoyaltyPage')) },
              { path: 'channels', ...lazy(() => import('./pages/dashboard/shop/ChannelsPage')) },
              { path: 'courses', ...lazy(() => import('./pages/dashboard/shop/CoursesPage')) },
              { path: 'plugins', ...lazy(() => import('./pages/dashboard/shop/PluginsPage')) },
              { path: 'live', ...lazy(() => import('./pages/dashboard/shop/LivePage')) },
              { path: 'templates', ...lazy(() => import('./pages/dashboard/shop/TemplatesPage')) },
              { path: 'import', ...lazy(() => import('./pages/dashboard/shop/ImportPage')) },
              { path: 'settings', ...lazy(() => import('./pages/dashboard/shop/SettingsPage')) },
              { path: 'analytics', ...lazy(() => import('./pages/dashboard/shop/AnalyticsPage')) },
            ],
          },
          { path: 'editor/shop/:shopId', ...lazy(() => import('./pages/dashboard/ShopEditor')) },
          { path: 'editor/site/:siteId', ...lazy(() => import('./pages/dashboard/SiteEditor')) },
          { path: 'site/:siteId/analytics', ...lazy(() => import('./pages/dashboard/SiteAnalytics')) },
          { path: 'site/:siteId/email-analytics', ...lazy(() => import('./pages/dashboard/EmailAnalytics')) },
          { path: 'site/:siteId/import/substack', ...lazy(() => import('./pages/dashboard/SubstackImport')) },
          { path: 'site/:siteId/sequences', ...lazy(() => import('./pages/dashboard/EmailSequences')) },
          { path: 'media', ...lazy(() => import('./pages/dashboard/Media')) },
          { path: 'my-orders', ...lazy(() => import('./pages/dashboard/MyOrders')) },
          { path: 'my-returns', ...lazy(() => import('./pages/dashboard/MyReturns')) },
          { path: 'my-addresses', ...lazy(() => import('./pages/dashboard/MyAddresses')) },
          { path: 'my-downloads', ...lazy(() => import('./pages/dashboard/MyDownloads')) },
          { path: 'my-store-credits', ...lazy(() => import('./pages/dashboard/MyStoreCredits')) },
          { path: 'wishlist', ...lazy(() => import('./pages/dashboard/Wishlist')) },
          { path: 'my-courses', ...lazy(() => import('./pages/dashboard/MyCourses')) },
          { path: 'teaching', ...lazy(() => import('./pages/dashboard/Teaching')) },
          { path: 'teaching/new', ...lazy(() => import('./pages/dashboard/CourseEditor')) },
          { path: 'teaching/:courseId', ...lazy(() => import('./pages/dashboard/CourseEditor')) },
          { path: 'course-wishlist', ...lazy(() => import('./pages/MyWishlist')) },
          { path: 'courses/:courseId', ...lazy(() => import('./pages/CourseLearn')) },
        ],
      },

      // ── Admin (mgt) ──
      {
        path: 'mgt',
        ...lazy(() => import('./layouts/AdminLayout')),
        children: [
          { index: true, ...lazy(() => import('./pages/mgt/Overview')) },
          { path: 'ops', ...lazy(() => import('./pages/mgt/OpsCenter')) },
          { path: 'users', ...lazy(() => import('./pages/mgt/Users')) },
          { path: 'hubs', ...lazy(() => import('./pages/mgt/Hubs')) },
          { path: 'analytics', ...lazy(() => import('./pages/mgt/Analytics')) },
          { path: 'reports', ...lazy(() => import('./pages/mgt/Reports')) },
          { path: 'security', ...lazy(() => import('./pages/mgt/Security')) },
          { path: 'ai-security', ...lazy(() => import('./pages/mgt/AISecurity')) },
          { path: 'ai-cost', ...lazy(() => import('./pages/mgt/AICost')) },
          { path: 'ai-models', ...lazy(() => import('./pages/mgt/AIModels')) },
          { path: 'model-catalog', ...lazy(() => import('./pages/mgt/ModelCatalog')) },
          { path: 'performance', ...lazy(() => import('./pages/mgt/Performance')) },
          { path: 'thumbnails', ...lazy(() => import('./pages/mgt/Thumbnails')) },
          { path: 'pipelines', ...lazy(() => import('./pages/mgt/Pipelines')) },
          { path: 'moderation', ...lazy(() => import('./pages/mgt/Moderation')) },
          { path: 'stickers', ...lazy(() => import('./pages/mgt/StickerReview')) },
          { path: 'audit', ...lazy(() => import('./pages/mgt/Audit')) },
          { path: 'billing', ...lazy(() => import('./pages/mgt/Billing')) },
          { path: 'settings', ...lazy(() => import('./pages/mgt/Settings')) },
          // /mgt/waitlist used to render the legacy referral-code
          // waitlist table. New beta signups go to invite_requests
          // (rendered by Invites.tsx) so we alias the old path to
          // avoid showing an empty page when admins land here via
          // bookmark or direct URL.
          { path: 'waitlist', ...lazy(() => import('./pages/mgt/Invites')) },
          { path: 'invitations', ...lazy(() => import('./pages/mgt/Invitations')) },
          { path: 'chat-audit', ...lazy(() => import('./pages/mgt/ChatAudit')) },
          { path: 'prompts', ...lazy(() => import('./pages/mgt/Prompts')) },
          { path: 'news-queue', ...lazy(() => import('./pages/mgt/NewsQueue')) },
          { path: 'news-sources', ...lazy(() => import('./pages/mgt/NewsSources')) },
          { path: 'content-bots', ...lazy(() => import('./pages/mgt/ContentBots')) },
          { path: 'bot-topics', ...lazy(() => import('./pages/mgt/BotTopics')) },
          { path: 'bans', ...lazy(() => import('./pages/mgt/Bans')) },
          { path: 'nsfw-review', ...lazy(() => import('./pages/mgt/NsfwReview')) },
          { path: 'takedowns', ...lazy(() => import('./pages/mgt/Takedowns')) },
          { path: 'quarantine', ...lazy(() => import('./pages/mgt/MediaQuarantine')) },
          { path: 'library-review', ...lazy(() => import('./pages/mgt/LibraryReview')) },
          { path: 'invites', ...lazy(() => import('./pages/mgt/Invites')) },
          { path: 'plans', ...lazy(() => import('./pages/mgt/Plans')) },
          { path: 'revenue', ...lazy(() => import('./pages/mgt/Revenue')) },
          { path: 'finance', ...lazy(() => import('./pages/mgt/Finance')) },
          { path: 'creator-fund', ...lazy(() => import('./pages/mgt/CreatorFund')) },
          { path: 'click-security', ...lazy(() => import('./pages/mgt/ClickSecurity')) },
          { path: 'template-queue', ...lazy(() => import('./pages/mgt/TemplateQueue')) },
          { path: 'plugin-queue', ...lazy(() => import('./pages/mgt/PluginQueue')) },
          { path: 'courses', ...lazy(() => import('./pages/mgt/Courses')) },
          { path: 'shops', ...lazy(() => import('./pages/mgt/Shops')) },
          { path: 'sites', ...lazy(() => import('./pages/mgt/Sites')) },
          { path: 'partners', ...lazy(() => import('./pages/mgt/Partners')) },
          { path: 'categories', ...lazy(() => import('./pages/mgt/Categories')) },
          { path: 'pricing', ...lazy(() => import('./pages/mgt/Pricing')) },
          { path: 'active-users', ...lazy(() => import('./pages/mgt/ActiveUsers')) },
          { path: 'user-profile', ...lazy(() => import('./pages/mgt/UserProfile')) },
          { path: 'user-profile/:userId', ...lazy(() => import('./pages/mgt/UserProfile')) },
          { path: 'verification-queue', ...lazy(() => import('./pages/mgt/VerificationQueue')) },
          { path: 'feature-flags', ...lazy(() => import('./pages/mgt/FeatureFlags')) },
          { path: 'platform-health', ...lazy(() => import('./pages/mgt/PlatformHealth')) },
          { path: 'ai-costs', ...lazy(() => import('./pages/mgt/AICosts')) },
          { path: 'monitoring', ...lazy(() => import('./pages/mgt/Monitoring')) },
          { path: 'user-activity', ...lazy(() => import('./pages/mgt/UserActivity')) },
        ],
      },
    ],
  },
  // Bare / → auto-detect browser language
  { path: '/', element: <BrowserLocaleRedirect /> , errorElement: <RouteErrorBoundary /> },
  // OIDC authorize endpoint as partners link it (no locale) → the user's locale,
  // query kept. A static path, so it outranks '/:locale' (which would read
  // "oauth" as a locale).
  { path: '/oauth/authorize', element: <BrowserLocaleRedirect suffix="/oauth/authorize" />, errorElement: <RouteErrorBoundary /> },
  // Template preview (no locale prefix)
  { path: '/templates/:slug', ...lazy(() => import('./pages/TemplatePreview')) , errorElement: <RouteErrorBoundary /> },
  { path: '/templates/:slug/:pageSlug', ...lazy(() => import('./pages/TemplatePreview')) , errorElement: <RouteErrorBoundary /> },
  // Catch-all: add the locale prefix — or, when the path already carries one
  // and still matches nothing, answer 404 instead of redirecting to itself.
  { path: '*', element: <LocaleRedirect /> , loader: catchAllLoader, errorElement: <RouteErrorBoundary /> },
]);

// A newer build is deployed: turn the next move to another page into a plain
// full page load of the destination, so this old tab never asks for a chunk
// the deploy removed (that was the brief "Something went wrong" + reload the
// founder saw). Same-page changes (filters, query) stay in-app; never while
// uploads run (lib/newVersion, lib/chunkReload).
router.subscribe((state) => {
  const next = state.navigation.location;
  if (state.navigation.state === 'idle' || !next || next.pathname === state.location.pathname) return;
  if (!isStaleBuild() || reloadBlocked()) return;
  window.location.assign(next.pathname + next.search + next.hash);
});
